import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { z } from 'zod';
import { useMe } from '../api/auth';
import { ApiError } from '../api/client';
import type { Event } from '../api/events';
import {
  SHORT_CODE_TAKEN,
  SHORT_CODE_TAKEN_HINT,
  useArchiveEvent,
  useEvent,
  useNewEventCode,
  usePatchEvent,
  usePublishEvent,
  useUnarchiveEvent,
  useUnpublishEvent,
  WEAK_PIN,
} from '../api/events';
import { useProducts } from '../api/products';
import { useStripeAccounts } from '../api/stripeAccounts';
import { Field } from '../components/Field';
import { StatusPill } from '../components/StatusPill';

const TIMEZONES = [
  'America/Los_Angeles',
  'America/Denver',
  'America/Chicago',
  'America/New_York',
  'America/Toronto',
  'Europe/London',
  'Asia/Tokyo',
  'Australia/Sydney',
  'UTC',
];

const setupSchema = z
  .object({
    name: z.string().min(1, 'Required'),
    shortCode: z
      .string()
      .trim()
      .transform((s) => s.toUpperCase())
      .pipe(z.string().regex(/^[A-Z0-9]{1,8}$/, 'Letters and digits only, max 8')),
    startsOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Required'),
    endsOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Required'),
    venue: z.string().nullable(),
    city: z.string().nullable(),
    timezone: z.string().min(1),
    pin: z.string().regex(/^\d{6}$/, 'Must be 6 digits'),
    allowCustomAmount: z.boolean(),
    allowOffhoursAuth: z.boolean(),
    allowCashPayments: z.boolean(),
    // Empty = leave the existing cash PIN unchanged. Non-empty = set a new
    // PIN. The "Clear" button is a separate state path (clearCashPin flag)
    // that doesn't go through this field.
    cashPinInput: z
      .string()
      .refine((s) => s === '' || /^\d{6}$/.test(s), {
        message: 'Must be 6 digits',
      }),
    // "Cash without PIN": the explicit choice the API requires when cash is
    // turned on for an event with no cash PIN (422 cash_pin_choice_required).
    // Checked sends `cashPin: null`.
    cashWithoutPin: z.boolean(),
    internalNotes: z.string().nullable(),
    // Which Stripe account this event's payments belong to (uuid FK to
    // stripe_accounts). Editable during onboarding; switching it post-launch
    // is allowed but the form surfaces a warning (see below).
    stripeAccountId: z.string().min(1, 'Required'),
    // tml_xxx Terminal location. Bound to a single Stripe account, so the
    // operator must re-enter it whenever they switch accounts.
    stripeLocationId: z.string().regex(/^tml_[A-Za-z0-9]+$/, 'must match tml_xxx'),
  })
  .refine((d) => d.endsOn >= d.startsOn, {
    message: 'Must be on or after First day',
    path: ['endsOn'],
  });

type SetupValues = z.infer<typeof setupSchema>;

/** Save errors the operator fixes in place: keep their input rather than reset the form. */
const FIXABLE = new Set(['short_code_taken', 'weak_pin', 'cash_pin_choice_required']);

function eventToFormValues(e: Event): SetupValues {
  return {
    name: e.name,
    shortCode: e.shortCode,
    startsOn: e.startsOn,
    endsOn: e.endsOn,
    venue: e.venue,
    city: e.city,
    timezone: e.timezone,
    pin: e.pin,
    allowCustomAmount: e.allowCustomAmount,
    allowOffhoursAuth: e.allowOffhoursAuth,
    allowCashPayments: e.allowCashPayments,
    cashPinInput: '',
    cashWithoutPin: false,
    internalNotes: e.internalNotes,
    stripeAccountId: e.stripeAccountId,
    stripeLocationId: e.stripeLocationId,
  };
}

/** Shared by the Publish, Return to draft and Unarchive buttons. */
function eventActionErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'short_code_taken') {
      // Unarchive re-checks the code against the active events.
      return `${SHORT_CODE_TAKEN} Give this event a new code, then try again.`;
    }
    if (err.code === 'event_archived') {
      return 'This event is archived. Unarchive it before publishing.';
    }
  }
  return 'Something went wrong. Try again.';
}

function eventStatus(e: Event): 'live' | 'upcoming' | 'ended' | 'draft' {
  if (!e.publishedAt) return 'draft';
  const today = new Date().toISOString().slice(0, 10);
  if (today < e.startsOn) return 'upcoming';
  if (today > e.endsOn) return 'ended';
  return 'live';
}

export function EventSetup() {
  const { eventId = '' } = useParams();
  const navigate = useNavigate();
  const eventQuery = useEvent(eventId);
  const productsQuery = useProducts(eventId);
  const patchEvent = usePatchEvent(eventId);
  const archiveEvent = useArchiveEvent();
  const unarchiveEvent = useUnarchiveEvent();
  const publishEvent = usePublishEvent();
  const unpublishEvent = useUnpublishEvent();
  const newEventCode = useNewEventCode();
  const meQuery = useMe();
  const stripeAccountsQuery = useStripeAccounts();
  const qc = useQueryClient();
  const [showPin, setShowPin] = useState(false);
  const [showCashPin, setShowCashPin] = useState(false);
  // Explicit "clear the cash PIN" intent — separate from the input field so
  // operators can wipe an existing PIN without typing anything new.
  const [clearCashPin, setClearCashPin] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [topLevelError, setTopLevelError] = useState<string | null>(null);
  const [publishError, setPublishError] = useState<string | null>(null);
  // Covers the whole confirm → save → publish sequence. `isSubmitting` only
  // tracks handleSubmit, which the publish path deliberately bypasses, so
  // without this a second click could start a parallel sequence.
  const [publishing, setPublishing] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [newCodeError, setNewCodeError] = useState<string | null>(null);

  const event = eventQuery.data;
  // The event PIN is a device credential; the API restricts changing it to
  // owners (403 pin_owner_only), so don't offer managers an editable field.
  //
  // Keyed on a positive "is a manager" rather than "is an owner": if /me fails,
  // the role is unknown, and the harmful default is the one that silently drops
  // `pin` from the save. Better to send it and let the server answer — it is
  // the authority here, and it returns a message the operator can act on.
  const canEditPin = meQuery.data?.role !== 'manager';

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    getValues,
    setError,
    trigger,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<SetupValues>({
    resolver: zodResolver(setupSchema),
  });

  // One-shot: the next [event] change is our own cache rollback after a
  // fixable save error, not new server data, so it must not wipe the form.
  // (Not keyed on object identity: structural sharing copies cache writes.)
  const keepFormOnNextEvent = useRef(false);
  // Unsaved edits to re-apply over the next reset. Set by "New random code":
  // its new event resets the form, which must replace the code but keep any
  // other edits (e.g. a renamed event whose save just hit short_code_taken).
  const carryEdits = useRef<Partial<SetupValues> | null>(null);

  useEffect(() => {
    if (!event) return;
    if (keepFormOnNextEvent.current) {
      keepFormOnNextEvent.current = false;
      return;
    }
    reset(eventToFormValues(event));
    const edits = carryEdits.current;
    carryEdits.current = null;
    if (edits) {
      for (const [field, value] of Object.entries(edits) as [keyof SetupValues, never][]) {
        setValue(field, value, { shouldDirty: true });
      }
    }
  }, [event, reset, setValue]);

  const allowCustomAmount = watch('allowCustomAmount');
  const allowOffhoursAuth = watch('allowOffhoursAuth');
  const allowCashPayments = watch('allowCashPayments');
  const cashPinInput = watch('cashPinInput');
  const cashWithoutPin = watch('cashWithoutPin');
  // The API wants an explicit cash-PIN choice only when cash goes from stored
  // off to on with no cash PIN, so the "Cash without PIN" control appears for
  // exactly that transition.
  const offerCashWithoutPin =
    event !== undefined && allowCashPayments && !event.allowCashPayments && !event.cashPinSet;
  const watchedStripeAccountId = watch('stripeAccountId');
  // True iff the picker has moved off the saved value. Surfaces the
  // "historical transactions stay tagged" warning + nudges the operator to
  // re-enter the Terminal location (tml_xxx is account-bound).
  const stripeAccountChanged =
    event !== undefined && watchedStripeAccountId !== event.stripeAccountId;

  useEffect(() => {
    if (savedAt === null) return;
    const t = setTimeout(() => setSavedAt(null), 2500);
    return () => clearTimeout(t);
  }, [savedAt]);

  /**
   * The Save path, minus the form plumbing. Returns whether the write landed,
   * so Publish can save first and abort if that save fails rather than
   * publishing values the operator believes they changed.
   */
  const saveValues = async (values: SetupValues): Promise<boolean> => {
    setTopLevelError(null);
    if (!event) return false;
    const key = ['event', eventId] as const;
    const previous = qc.getQueryData<Event>(key);

    // Strip the virtual cashPinInput before optimistic-merging into the cache:
    // Event has cashPinSet (boolean), not the raw input. The mutation body
    // also splits cashPinInput vs cashPin (sent to API).
    const { cashPinInput, cashWithoutPin, ...rest } = values;
    const base = previous ?? event;
    const optimistic: Event = {
      ...base,
      ...rest,
      cashPinSet: clearCashPin ? false : cashPinInput.length > 0 ? true : base.cashPinSet,
    };
    qc.setQueryData<Event | undefined>(key, optimistic);

    // Build the patch body. cashPinInput="" + !clearCashPin => omit cashPin
    // entirely, leaving the existing hash untouched server-side.
    const patchBody: Parameters<typeof patchEvent.mutateAsync>[0] = { ...rest };
    // The PIN is owner-only server-side. Managers submit the whole form, so
    // drop the field rather than echo a value they can't change — otherwise
    // the next owner PIN rotation would 403 every manager save until they
    // reloaded the stale form.
    if (!canEditPin) delete patchBody.pin;
    if (clearCashPin) {
      patchBody.cashPin = null;
    } else if (
      cashWithoutPin &&
      values.allowCashPayments &&
      !base.allowCashPayments &&
      !base.cashPinSet
    ) {
      patchBody.cashPin = null;
    } else if (cashPinInput.length > 0) {
      patchBody.cashPin = cashPinInput;
    }

    try {
      await patchEvent.mutateAsync(patchBody);
      setSavedAt(Date.now());
      setClearCashPin(false);
      reset({ ...values, cashPinInput: '' });
      return true;
    } catch (err) {
      // Rollback the cache. For errors the operator fixes in place, keep the
      // form (code, PIN, cash toggle) as typed; otherwise reset it.
      const fixable = err instanceof ApiError && FIXABLE.has(err.code);
      if (previous) {
        if (fixable && qc.getQueryData<Event>(key) !== previous) keepFormOnNextEvent.current = true;
        qc.setQueryData<Event>(key, previous);
      }
      if (!fixable) reset(eventToFormValues(base));
      setClearCashPin(false);
      if (fixable) {
        // The optimistic write already reset the form to these values, making
        // them the defaults. Point the defaults back at the stored event so the
        // form reads dirty (Publish then saves first), and restore the typed
        // cash PIN, which the optimistic reset blanked.
        reset(eventToFormValues(base), { keepValues: true });
        setValue('cashPinInput', cashPinInput, { shouldDirty: true });
      }
      if (err instanceof ApiError && err.code === 'short_code_taken') {
        setError('shortCode', { type: 'short_code_taken', message: SHORT_CODE_TAKEN });
        return false;
      }
      if (err instanceof ApiError && err.code === 'weak_pin') {
        // The PIN that was rejected: the cash PIN if only it was being set.
        const pinChanged = patchBody.pin !== undefined && patchBody.pin !== base.pin;
        const cashPinSent = typeof patchBody.cashPin === 'string';
        setError(cashPinSent && !pinChanged ? 'cashPinInput' : 'pin', { message: WEAK_PIN });
        return false;
      }
      let message: string;
      if (err instanceof ApiError) {
        switch (err.code) {
          case 'invalid_request':
            message = "Some fields aren't valid — check your input.";
            break;
          case 'stripe_account_not_found':
            message =
              'That Stripe account no longer exists. Reload the page and pick another.';
            break;
          case 'location_required_with_account':
            message =
              'Switching Stripe account requires a new Terminal location (tml_xxx).';
            break;
          case 'cash_pin_choice_required':
            message = 'Set a 6-digit cash PIN, or choose Cash without PIN.';
            break;
          case 'pin_owner_only':
            // Defensive: managers' saves omit `pin` entirely, so this only
            // shows up if the form state and the user's role disagree.
            message = 'Only owners can change the event PIN. Reload the page and try again.';
            break;
          default:
            message = 'Something went wrong. Try again.';
        }
      } else {
        message = 'Something went wrong. Try again.';
      }
      setTopLevelError(message);
      return false;
    }
  };

  const onSubmit = handleSubmit(async (values) => {
    await saveValues(values);
  });

  // Waits on /me too: the role decides whether the PIN field is editable and
  // whether `pin` is sent at all, so rendering the form before it resolves
  // flashes owners a read-only field and can drop the field from a fast save.
  if (eventQuery.isLoading || meQuery.isLoading || !event) {
    return (
      <div className="card-pad">
        <div>Loading…</div>
      </div>
    );
  }

  const products = productsQuery.data ?? [];
  const activeProducts = products.filter((p) => !p.archivedAt);
  const detailsDone = Boolean(
    event.startsOn && event.endsOn && event.venue && event.city && event.timezone,
  );
  const pinDone = Boolean(event.pin);
  const catalogDone = activeProducts.length > 0;

  return (
    <div className="card-pad">
      <div className="tabs" role="tablist">
        <button type="button" className="tab active" role="tab" aria-selected="true">
          Details
        </button>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) 320px',
          gap: 24,
          alignItems: 'start',
        }}
      >
        <div className="card card-pad">
          <h3>Event details</h3>
          <form onSubmit={onSubmit} noValidate style={{ display: 'grid', gap: 16, marginTop: 12 }}>
            <Field label="Event name" error={errors.name?.message}>
              <input type="text" {...register('name')} />
            </Field>
            <Field
              label="Short code"
              error={errors.shortCode?.message ?? newCodeError ?? undefined}
              help={
                errors.shortCode?.type === 'short_code_taken'
                  ? SHORT_CODE_TAKEN_HINT
                  : 'Up to 8 characters; shown on receipts.'
              }
            >
              <div style={{ display: 'flex', gap: 8 }}>
                <input type="text" maxLength={8} style={{ flex: 1 }} {...register('shortCode')} />
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  // The server refuses a code swap on an archived event (403);
                  // unarchiving re-checks the code instead.
                  disabled={newEventCode.isPending || Boolean(event.archivedAt)}
                  title={event.archivedAt ? 'Unarchive the event to change its code' : undefined}
                  onClick={async () => {
                    setNewCodeError(null);
                    // The new event resets the form; carry the other unsaved
                    // edits across it. The typed code is replaced, not kept.
                    // Diffed against the stored event rather than read from
                    // dirtyFields: the keepValues reset after a fixable save
                    // error clears dirtyFields while the edits remain.
                    const values = getValues();
                    const stored = eventToFormValues(event);
                    const edits: Partial<Record<keyof SetupValues, unknown>> = {};
                    for (const field of Object.keys(values) as (keyof SetupValues)[]) {
                      if (field !== 'shortCode' && values[field] !== stored[field]) {
                        edits[field] = values[field];
                      }
                    }
                    carryEdits.current = edits as Partial<SetupValues>;
                    try {
                      await newEventCode.mutateAsync(eventId);
                    } catch (err) {
                      carryEdits.current = null;
                      setNewCodeError(
                        err instanceof ApiError && err.status === 429
                          ? 'Too many code changes. Try again in a few minutes.'
                          : "Couldn't generate a new code. Try again.",
                      );
                    }
                  }}
                >
                  {newEventCode.isPending ? 'Generating…' : 'New random code'}
                </button>
              </div>
            </Field>
            <div className="grid-2">
              <Field label="First day" error={errors.startsOn?.message}>
                <input type="date" {...register('startsOn')} />
              </Field>
              <Field label="Last day" error={errors.endsOn?.message}>
                <input type="date" {...register('endsOn')} />
              </Field>
            </div>
            <div className="grid-2">
              <Field label="Venue" error={errors.venue?.message}>
                <input type="text" {...register('venue')} />
              </Field>
              <Field label="City" error={errors.city?.message}>
                <input type="text" {...register('city')} />
              </Field>
            </div>
            <Field label="Timezone" error={errors.timezone?.message}>
              <select {...register('timezone')}>
                {TIMEZONES.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Currency" help="Editing is deferred to event-creation flow.">
              <div
                style={{
                  padding: '10px 12px',
                  border: '1px solid var(--border-1)',
                  borderRadius: 8,
                  background: 'var(--bg-surface-2)',
                  color: 'var(--fg-2)',
                  fontSize: 14,
                }}
              >
                {event.currency}
              </div>
            </Field>
            <Field
              label="PIN"
              error={errors.pin?.message}
              help={canEditPin ? undefined : 'Only owners can change the event PIN.'}
            >
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type={showPin ? 'text' : 'password'}
                  inputMode="numeric"
                  maxLength={6}
                  style={{ flex: 1 }}
                  readOnly={!canEditPin}
                  {...register('pin')}
                />
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setShowPin((s) => !s)}
                  aria-label={showPin ? 'Hide PIN' : 'Show PIN'}
                >
                  {showPin ? 'Hide' : 'Show'}
                </button>
              </div>
            </Field>
            <div className="sub">
              Crew login: {event.shortCode} · {event.pin}
            </div>
            <Field label="Allow custom amount">
              <button
                type="button"
                role="switch"
                aria-checked={allowCustomAmount}
                aria-label="Allow custom amount"
                className={`toggle${allowCustomAmount ? ' on' : ''}`}
                onClick={() => setValue('allowCustomAmount', !allowCustomAmount, { shouldDirty: true })}
              />
              <input type="hidden" {...register('allowCustomAmount')} />
            </Field>
            <Field
              label="Allow off-hours auth"
              help="Permit logins outside the event window."
            >
              <button
                type="button"
                role="switch"
                aria-checked={allowOffhoursAuth}
                aria-label="Allow off-hours auth"
                className={`toggle${allowOffhoursAuth ? ' on' : ''}`}
                onClick={() => setValue('allowOffhoursAuth', !allowOffhoursAuth, { shouldDirty: true })}
              />
              <input type="hidden" {...register('allowOffhoursAuth')} />
            </Field>
            <Field
              label="Allow cash payments"
              help="Shows a CASH button on the device next to CHARGE."
            >
              <button
                type="button"
                role="switch"
                aria-checked={allowCashPayments}
                aria-label="Allow cash payments"
                className={`toggle${allowCashPayments ? ' on' : ''}`}
                onClick={() =>
                  setValue('allowCashPayments', !allowCashPayments, { shouldDirty: true })
                }
              />
              <input type="hidden" {...register('allowCashPayments')} />
            </Field>
            {allowCashPayments && (
              <Field
                label="Cash PIN (optional)"
                error={errors.cashPinInput?.message}
                help={
                  clearCashPin
                    ? 'Will clear the existing cash PIN on save.'
                    : event.cashPinSet
                      ? 'A cash PIN is configured. Type a new value to replace it, or click Clear.'
                      : 'Crew enters this PIN once per shift to take cash. 6 digits.'
                }
              >
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <input
                    type={showCashPin ? 'text' : 'password'}
                    inputMode="numeric"
                    maxLength={6}
                    placeholder={event.cashPinSet ? '••••••' : ''}
                    style={{ flex: 1, minWidth: 120 }}
                    disabled={clearCashPin || (offerCashWithoutPin && cashWithoutPin)}
                    {...register('cashPinInput')}
                  />
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => setShowCashPin((s) => !s)}
                    aria-label={showCashPin ? 'Hide cash PIN' : 'Show cash PIN'}
                    disabled={clearCashPin || cashPinInput.length === 0}
                  >
                    {showCashPin ? 'Hide' : 'Show'}
                  </button>
                  {event.cashPinSet && !clearCashPin && (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => {
                        setClearCashPin(true);
                        setValue('cashPinInput', '', { shouldDirty: true });
                      }}
                    >
                      Clear
                    </button>
                  )}
                  {clearCashPin && (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => setClearCashPin(false)}
                    >
                      Undo clear
                    </button>
                  )}
                </div>
                {offerCashWithoutPin && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                    <input
                      type="checkbox"
                      aria-label="Cash without PIN"
                      {...register('cashWithoutPin', {
                        onChange: (e) => {
                          if (e.target.checked) setValue('cashPinInput', '', { shouldValidate: true });
                        },
                      })}
                    />
                    Cash without PIN
                  </label>
                )}
              </Field>
            )}
            <Field
              label="Stripe account"
              error={errors.stripeAccountId?.message}
              help={
                event?.stripeMode
                  ? `Currently in ${event.stripeMode} mode (derived from the publishable key on the linked Stripe account).`
                  : 'Mode unknown — set the publishable key on the Stripe account first.'
              }
            >
              <select
                {...register('stripeAccountId', {
                  onChange: () => {
                    // tml_xxx is bound to a single Stripe account, so the
                    // operator must re-enter it for the new account. Clearing
                    // forces them to type a fresh value before submit.
                    setValue('stripeLocationId', '', { shouldDirty: true, shouldValidate: true });
                  },
                })}
              >
                {stripeAccountsQuery.data?.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.displayName} ({a.label})
                  </option>
                ))}
              </select>
            </Field>
            {stripeAccountChanged && (
              <div
                className="form-warning"
                style={{
                  padding: 12,
                  borderRadius: 8,
                  background: 'var(--oeg-lilac, #F2EBFA)',
                  color: 'var(--oeg-purple-ink, #4A2A7A)',
                  fontSize: 13,
                  lineHeight: 1.4,
                }}
              >
                <strong>Heads up.</strong> Switching the Stripe account changes
                where new payments land. Existing transactions on this event
                stay tagged with their original Stripe account, so refunds and
                reporting on those rows will continue to use the previous
                account. Best done during onboarding before any sales.
              </div>
            )}
            <Field
              label="Stripe Terminal location"
              help={
                stripeAccountChanged
                  ? 'tml_xxx for the new Stripe account. Find it in Stripe Dashboard → Terminal → Locations.'
                  : 'tml_xxx for this event. Bound to the selected Stripe account.'
              }
              error={errors.stripeLocationId?.message}
            >
              <input
                type="text"
                placeholder="tml_xxxxxxxxxxxxxxxx"
                autoComplete="off"
                {...register('stripeLocationId')}
              />
            </Field>
            <Field label="Internal notes" error={errors.internalNotes?.message}>
              <textarea rows={4} {...register('internalNotes')} />
            </Field>

            {topLevelError && <div className="form-error">{topLevelError}</div>}

            <div
              aria-live="polite"
              role="status"
              style={{ minHeight: 20, color: 'var(--oeg-success)', fontSize: 13 }}
            >
              {savedAt !== null ? 'Saved.' : ''}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                {isSubmitting ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>

          {meQuery.data?.role === 'owner' && (
            <div
              className="card card-pad"
              style={{ marginTop: 24, borderColor: 'var(--border-2)' }}
              data-testid="publish-card"
            >
              <h3>{event.publishedAt ? 'Published' : 'Publish event'}</h3>
              <p className="micro">
                {event.publishedAt
                  ? 'Crew can sign in with the event PIN. Returning it to draft signs devices out on their next request.'
                  : "Devices can't sign in with the event PIN until the event is published."}
              </p>
              {publishError && <div className="form-error">{publishError}</div>}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                {event.publishedAt ? (
                  <button
                    type="button"
                    className="btn"
                    onClick={async () => {
                      if (
                        !window.confirm(
                          `Return "${event.name}" to draft? Crew devices will be signed out and the event PIN will stop working.`,
                        )
                      ) {
                        return;
                      }
                      setPublishError(null);
                      try {
                        await unpublishEvent.mutateAsync(eventId);
                      } catch (err) {
                        setPublishError(eventActionErrorMessage(err));
                      }
                    }}
                    disabled={unpublishEvent.isPending}
                  >
                    {unpublishEvent.isPending ? 'Returning to draft…' : 'Return to draft'}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={async () => {
                      setPublishError(null);
                      setPublishing(true);
                      try {
                        // Publish acts on stored values, so unsaved edits would
                        // be silently left behind — the operator would publish
                        // the old PIN believing they had changed it. A pending
                        // cash-PIN clear counts too: it writes '' over a default
                        // of '', so react-hook-form's isDirty never sees it.
                        if (isDirty || clearCashPin) {
                          if (
                            !window.confirm(
                              'You have unsaved changes. Save them and publish? Cancel to keep editing.',
                            )
                          ) {
                            return;
                          }
                          if (!(await trigger())) {
                            // Field-level errors already render inline.
                            setPublishError('Fix the highlighted fields, then publish.');
                            return;
                          }
                          // Abort on a failed save; the top-level or field error
                          // says why. Parse, not raw getValues: the schema
                          // upper-cases the code.
                          if (!(await saveValues(setupSchema.parse(getValues())))) return;
                        }
                        try {
                          await publishEvent.mutateAsync(eventId);
                        } catch (err) {
                          setPublishError(eventActionErrorMessage(err));
                        }
                      } finally {
                        setPublishing(false);
                      }
                    }}
                    disabled={publishEvent.isPending || isSubmitting || publishing}
                  >
                    {publishEvent.isPending ? 'Publishing…' : 'Publish event'}
                  </button>
                )}
              </div>
            </div>
          )}

          {meQuery.data?.role === 'owner' && (
            <div
              className="card card-pad"
              style={{ marginTop: 24, borderColor: 'var(--border-2)' }}
            >
              <h3>{event.archivedAt ? 'Archived' : 'Archive event'}</h3>
              <p className="micro">
                {event.archivedAt
                  ? 'This event is archived. Restore it to make it visible to operators again.'
                  : 'Archives the event. Operators will no longer see it in the events list. Reversible.'}
              </p>
              {archiveError && <div className="form-error">{archiveError}</div>}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                {event.archivedAt ? (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={async () => {
                      setArchiveError(null);
                      try {
                        await unarchiveEvent.mutateAsync(eventId);
                      } catch (err) {
                        // Unarchiving can 409 short_code_taken if another active
                        // event took this code while it was archived.
                        setArchiveError(eventActionErrorMessage(err));
                      }
                    }}
                    disabled={unarchiveEvent.isPending}
                  >
                    {unarchiveEvent.isPending ? 'Restoring…' : 'Unarchive'}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={async () => {
                      setArchiveError(null);
                      if (!window.confirm(`Archive "${event.name}"? You can unarchive later.`)) {
                        return;
                      }
                      await archiveEvent.mutateAsync(eventId);
                      navigate('/events');
                    }}
                    disabled={archiveEvent.isPending}
                  >
                    {archiveEvent.isPending ? 'Archiving…' : 'Archive event'}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        <aside style={{ display: 'grid', gap: 16 }}>
          <section className="card card-pad" data-testid="right-now">
            <h3>Right now</h3>
            <div style={{ marginTop: 8, display: 'grid', gap: 8 }}>
              <div style={{ fontWeight: 600 }}>{event.name}</div>
              <div>
                <StatusPill status={eventStatus(event)} />
              </div>
              <div className="sub">
                {event.startsOn} – {event.endsOn}
              </div>
              {(event.venue || event.city) && (
                <div className="sub">
                  {[event.venue, event.city].filter(Boolean).join(', ')}
                </div>
              )}
            </div>
          </section>

          <section className="card card-pad" data-testid="setup-checklist">
            <h3>Setup checklist</h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0', display: 'grid', gap: 8 }}>
              <ChecklistItem testId="checklist-details" label="Details" done={detailsDone} />
              <ChecklistItem testId="checklist-pin" label="PIN" done={pinDone} />
              <ChecklistItem testId="checklist-catalog" label="Catalog" done={catalogDone} />
              <ChecklistItem testId="checklist-staff" label="Staff" done={false} comingSoon />
              <ChecklistItem testId="checklist-payments" label="Payments" done={false} comingSoon />
              <ChecklistItem
                testId="checklist-publish"
                label="Publish"
                done={Boolean(event.publishedAt)}
              />
            </ul>
          </section>
        </aside>
      </div>
    </div>
  );
}

interface ChecklistItemProps {
  label: string;
  done: boolean;
  comingSoon?: boolean;
  testId: string;
}

function ChecklistItem({ label, done, comingSoon, testId }: ChecklistItemProps) {
  return (
    <li
      data-testid={testId}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '6px 0',
        borderBottom: '1px solid var(--border-1)',
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span
          aria-hidden="true"
          style={{
            width: 18,
            height: 18,
            borderRadius: 999,
            display: 'inline-grid',
            placeItems: 'center',
            background: done ? 'var(--oeg-success)' : 'transparent',
            border: done ? 'none' : '1px solid var(--border-2)',
            color: '#fff',
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          {done ? '✓' : ''}
        </span>
        <span>{label}</span>
      </span>
      <span className="sub" style={{ fontSize: 12 }}>
        {comingSoon ? 'Coming soon' : done ? 'Done' : 'Pending'}
      </span>
    </li>
  );
}
